import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PoweroffOutlined, SyncOutlined, TranslationOutlined } from "@ant-design/icons";
import { Button, Col, Form, Modal, Progress, Radio, Row, Space, Switch, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { ETranslateOption, ETranslationFlag, TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField, TemplateMetaDataField, getSupportedLanguages } from "@devowl-wp/api-real-cookie-banner";
import { LanguageSelect, useLanguageOptions } from "./language-select.js";
import { UntranslatableHeader } from "./untranslatable-header.js";
import { UntranslatableManager } from "./untranslatable-manager.js";
import { useEntityMetadata } from "../../../../../../hooks/useEntityMetadata.js";
import { fetchTranslations } from "../../../../../../hooks/useTranslation.js";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { EFormEvent, FormEventBus } from "../../../../../../util/eventBus.js";
import { defaultTranslationFlexNamePath } from "../../../../../../util/template/form.js";
import { getTranslationFlagNamePath } from "../../../../../../util/translation.js";
import { StickyModeSwitch } from "../../../user/sticky-mode-switch.js";
import { FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME } from "../template-type.js";
// UntranslatableForm type removed as it's no longer needed
const FormFieldTemplateLanguages = observer(()=>{
    const { t } = useTranslation();
    const { rcbUserSettingsStore, rcbAuthenticationStore, notificationStore } = useStores();
    const { translationSettings: { isTranslationMode, sourceLanguage, targetLanguage } } = rcbUserSettingsStore.settings;
    const isTranslatorRole = rcbAuthenticationStore.session?.user?.role === EUserRole.Translator;
    const form = Form.useFormInstance();
    const rowProps = {
        gutter: [
            10,
            10
        ],
        style: {
            alignItems: "baseline",
            paddingBottom: "0.2rem",
            wordBreak: "keep-all"
        }
    };
    const [excludedSource, setExcludedSource] = useState([
        ...new Set([
            targetLanguage
        ]).values()
    ]);
    const [excludedTarget, setExcludedTarget] = useState([
        ...new Set([
            DEFAULT_LANGUAGE,
            sourceLanguage
        ]).values()
    ]);
    useEffect(()=>{
        setExcludedSource([
            ...new Set([
                targetLanguage
            ]).values()
        ]);
        setExcludedTarget([
            ...new Set([
                DEFAULT_LANGUAGE,
                sourceLanguage
            ]).values()
        ]);
    }, [
        targetLanguage,
        sourceLanguage
    ]);
    const templateType = form.getFieldValue(FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME);
    const { data: metaData, isSuccess } = useEntityMetadata(templateType === "Service" ? "ServiceTemplate" : templateType === "Content-Blocker" ? "ContentBlockerTemplate" : undefined);
    useEffect(()=>{
        if (metaData && isSuccess) {
            console.log(`Options`, {
                metadata: metaData.translatableOptions
            });
        }
    }, [
        metaData
    ]);
    const [translating, setIsTranslating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [translateOptionForm] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const showModal = ()=>{
        setIsModalVisible(true);
    };
    const hideModal = ()=>{
        setIsModalVisible(false);
        setSelectedLanguages([]);
        translateOptionForm.resetFields();
    };
    const languages = useMemo(()=>useLanguageOptions(t), [
        t
    ]);
    const createTranslationNotification = (type, titleKey, languageCodes)=>{
        if (languageCodes.length === 0) return;
        const languageLabels = languageCodes.map((langCode)=>languages.find((language)=>language.value === langCode)?.label).filter(Boolean).join(", ");
        notificationStore.add({
            type,
            title: t(titleKey),
            message: t(`Translations for ${languageLabels} ${type === "success" ? "inserted" : type === "info" ? "is not supported, yet" : "failed"}.`),
            closable: true
        });
    };
    const handleTranslate = async ()=>{
        setIsTranslating(true);
        setProgress(0);
        const failedLanguages = [];
        const successLanguages = [];
        const skippedLanguages = [];
        for(let i = 0; i < selectedLanguages.length; i++){
            try {
                const result = await translateTemplate(form, selectedLanguages[i], rcbAuthenticationStore, metaData.translatableOptions, translateOptionForm.getFieldsValue());
                if (result === "success") {
                    successLanguages.push(selectedLanguages[i]);
                } else if (result === "skip") {
                    skippedLanguages.push(selectedLanguages[i]);
                }
            } catch (error) {
                failedLanguages.push(selectedLanguages[i]);
            } finally{
                setProgress(Math.round((i + 1) / selectedLanguages.length * 100));
            }
        }
        setIsTranslating(false);
        hideModal();
        createTranslationNotification("success", "Machine translation completed", successLanguages);
        createTranslationNotification("info", "Machine translation skipped", skippedLanguages);
        createTranslationNotification("error", "Machine translation failed", failedLanguages);
        FormEventBus.publish(EFormEvent.TRANSLATION_FLAGS_CHANGED);
    };
    const availableLanguages = getSupportedLanguages(false).filter((lang)=>lang !== DEFAULT_LANGUAGE);
    const languageSelectStyle = {
        minWidth: "11rem"
    };
    const initialTranslateOptions = useMemo(()=>{
        const getDefaultOption = (fieldName)=>{
            if (!metaData?.translatableOptions) return ETranslateOption.Copy;
            const option = metaData.translatableOptions.find((opt)=>opt.propertyName === fieldName);
            return option?.defaultTranslateOption ?? ETranslateOption.Copy;
        };
        return {
            name: getDefaultOption(TemplateMetaDataField.Name),
            headline: getDefaultOption(TemplateMetaDataField.Headline),
            subHeadline: getDefaultOption(TemplateMetaDataField.SubHeadline),
            provider: templateType === "Service" ? getDefaultOption(ServiceTemplateField.Provider) : ETranslateOption.Hidden,
            providerPrivacyPolicyUrl: templateType === "Service" ? getDefaultOption(ServiceTemplateField.ProviderPrivacyPolicyUrl) : ETranslateOption.Hidden,
            providerLegalNoticeUrl: templateType === "Service" ? getDefaultOption(ServiceTemplateField.ProviderLegalNoticeUrl) : ETranslateOption.Hidden
        };
    }, [
        metaData,
        templateType
    ]);
    return /*#__PURE__*/ _jsx(Form.Item, {
        label: t("Translation Mode"),
        children: /*#__PURE__*/ _jsxs(Row, {
            ...rowProps,
            gutter: 16,
            children: [
                /*#__PURE__*/ _jsx(Col, {
                    children: /*#__PURE__*/ _jsx(Switch, {
                        checked: isTranslationMode,
                        onChange: (checked)=>{
                            rcbUserSettingsStore.updateTranslationSettings({
                                isTranslationMode: checked
                            });
                        },
                        checkedChildren: /*#__PURE__*/ _jsx(_Fragment, {
                            children: "I"
                        }),
                        unCheckedChildren: /*#__PURE__*/ _jsx(PoweroffOutlined, {})
                    })
                }),
                /*#__PURE__*/ _jsx(Col, {
                    hidden: !isTranslationMode,
                    flex: "1",
                    style: {
                        textAlign: "center"
                    },
                    children: /*#__PURE__*/ _jsxs(Space, {
                        direction: "horizontal",
                        children: [
                            /*#__PURE__*/ _jsx(LanguageSelect, {
                                value: sourceLanguage,
                                excludedLang: excludedSource,
                                onChange: (sourceValue)=>{
                                    rcbUserSettingsStore.updateTranslationSettings({
                                        sourceLanguage: sourceValue
                                    });
                                },
                                style: languageSelectStyle
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                hidden: sourceLanguage === DEFAULT_LANGUAGE,
                                type: "link",
                                icon: /*#__PURE__*/ _jsx(SyncOutlined, {}),
                                onClick: ()=>{
                                    const switchLanguages = ()=>{
                                        const currentSource = sourceLanguage;
                                        const currentTarget = targetLanguage;
                                        rcbUserSettingsStore.updateTranslationSettings({
                                            sourceLanguage: currentTarget,
                                            targetLanguage: currentSource
                                        });
                                    };
                                    switchLanguages();
                                }
                            }),
                            /*#__PURE__*/ _jsx(LanguageSelect, {
                                value: targetLanguage,
                                excludedLang: excludedTarget,
                                onChange: (targetValue)=>{
                                    rcbUserSettingsStore.updateTranslationSettings({
                                        targetLanguage: targetValue
                                    });
                                },
                                style: languageSelectStyle
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(Col, {
                    hidden: !isTranslationMode || isTranslatorRole,
                    style: {
                        textAlign: "right"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            icon: /*#__PURE__*/ _jsx(TranslationOutlined, {}),
                            onClick: showModal,
                            children: t("Machine Translate")
                        }),
                        /*#__PURE__*/ _jsxs(Modal, {
                            title: t("Machine Translation"),
                            open: isModalVisible,
                            onCancel: hideModal,
                            closable: !translating,
                            destroyOnClose: true,
                            centered: true,
                            footer: [
                                /*#__PURE__*/ _jsx(Button, {
                                    disabled: translating,
                                    onClick: hideModal,
                                    children: t("Cancel")
                                }, "cancel"),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    loading: translating,
                                    onClick: handleTranslate,
                                    disabled: selectedLanguages.length === 0,
                                    children: t("Translate")
                                }, "translate")
                            ],
                            style: {
                                minWidth: "60%"
                            },
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    style: {
                                        marginBottom: "20px"
                                    },
                                    gutter: [
                                        10,
                                        0
                                    ],
                                    children: [
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: "1",
                                            children: /*#__PURE__*/ _jsx(LanguageSelect, {
                                                disabled: translating,
                                                mode: "multiple",
                                                style: {
                                                    width: "100%",
                                                    marginBottom: "20px"
                                                },
                                                placeholder: t("Select languages"),
                                                value: selectedLanguages,
                                                onChange: (values)=>setSelectedLanguages(values),
                                                excludedLang: [
                                                    DEFAULT_LANGUAGE
                                                ],
                                                allowClear: true
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: "none",
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                disabled: selectedLanguages.length === availableLanguages.length || translating,
                                                onClick: ()=>{
                                                    setSelectedLanguages(availableLanguages);
                                                },
                                                children: t("Select all languages")
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(Col, {
                                            flex: "1",
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography.Title, {
                                                    level: 5,
                                                    children: t("Translation options")
                                                }),
                                                /*#__PURE__*/ _jsx(Form, {
                                                    form: translateOptionForm,
                                                    initialValues: initialTranslateOptions,
                                                    children: /*#__PURE__*/ _jsx(Space, {
                                                        direction: "vertical",
                                                        children: /*#__PURE__*/ _jsxs(Row, {
                                                            gutter: [
                                                                16,
                                                                16
                                                            ],
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Col, {
                                                                    children: /*#__PURE__*/ _jsx(Row, {
                                                                        gutter: [
                                                                            16,
                                                                            16
                                                                        ],
                                                                        children: /*#__PURE__*/ _jsxs(Col, {
                                                                            span: 24,
                                                                            children: [
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Headline")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "headline",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Translate,
                                                                                                        children: t("Translate")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Sub Headline")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "subHeadline",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Translate,
                                                                                                        children: t("Translate")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Name")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "name",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Translate,
                                                                                                        children: t("Translate")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                })
                                                                            ]
                                                                        })
                                                                    })
                                                                }),
                                                                /*#__PURE__*/ _jsx(Col, {
                                                                    children: /*#__PURE__*/ _jsx(Row, {
                                                                        hidden: templateType !== "Service",
                                                                        gutter: [
                                                                            16,
                                                                            16
                                                                        ],
                                                                        children: /*#__PURE__*/ _jsxs(Col, {
                                                                            span: 24,
                                                                            children: [
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Provider")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "provider",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Provider Policy URL")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "providerPrivacyPolicyUrl",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    style: {
                                                                                        marginBottom: 16
                                                                                    },
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("div", {
                                                                                            style: {
                                                                                                marginBottom: 8
                                                                                            },
                                                                                            children: t("Provider Legal Notice URL")
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                                                            name: "providerLegalNoticeUrl",
                                                                                            noStyle: true,
                                                                                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                                                                                optionType: "button",
                                                                                                buttonStyle: "solid",
                                                                                                disabled: translating,
                                                                                                children: [
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Copy,
                                                                                                        children: t("Copy")
                                                                                                    }),
                                                                                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                                                                                        value: ETranslateOption.Skip,
                                                                                                        children: t("Skip")
                                                                                                    })
                                                                                                ]
                                                                                            })
                                                                                        })
                                                                                    ]
                                                                                })
                                                                            ]
                                                                        })
                                                                    })
                                                                })
                                                            ]
                                                        })
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Col, {
                                            flex: "1",
                                            children: [
                                                /*#__PURE__*/ _jsx(UntranslatableHeader, {}),
                                                /*#__PURE__*/ _jsx(UntranslatableManager, {
                                                    templateType: templateType,
                                                    templateIdentifier: form.getFieldValue("identifier")
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Row, {
                                    hidden: !translating,
                                    children: /*#__PURE__*/ _jsx(Col, {
                                        flex: "1",
                                        children: /*#__PURE__*/ _jsx(Progress, {
                                            percent: progress,
                                            status: "active",
                                            strokeWidth: 10,
                                            format: (percent)=>`${percent}%`,
                                            style: {
                                                width: "100%",
                                                margin: "0 auto"
                                            }
                                        })
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Col, {
                    children: /*#__PURE__*/ _jsx(StickyModeSwitch, {})
                })
            ]
        })
    });
});
function extractTranslatableValues(options, values) {
    const result = {};
    function processValue(option, value, prefix = "") {
        if (typeof value === "string" && value.trim() !== "") {
            result[prefix] = value;
        } else if (option.isRelation && Array.isArray(value)) {
            value.forEach((item, index)=>{
                option.relationOptions?.forEach((relOption)=>{
                    processValue(relOption, item[relOption.propertyName], `${prefix}.${index}.${relOption.propertyName}`);
                });
            });
        } else if (typeof value === "object" && value !== null) {
            Object.keys(value).forEach((key)=>{
                const keyValue = value[key];
                if (typeof keyValue === "string" && keyValue !== null) {
                    processValue({
                        propertyName: key
                    }, keyValue, prefix ? `${prefix}.${key}` : key);
                }
            });
        }
    }
    options.forEach((option)=>{
        processValue(option, values[option.propertyName], option.propertyName);
    });
    return result;
}
/**
 * Translate template fields, using the machine translation API and metadata from our backend.
 *
 * @param form
 * @param targetLanguage
 * @param rcbAuthenticationStore
 * @param metadata
 * @param translateOptions
 * @returns
 */ async function translateTemplate(form, targetLanguage, rcbAuthenticationStore, metadata, translateOptions) {
    if (targetLanguage === DEFAULT_LANGUAGE) return "default";
    const result = await handleMachineTranslation(form, targetLanguage, rcbAuthenticationStore, metadata, translateOptions);
    if (result === "skip") return "skip";
    handleFieldCopying(form, targetLanguage, translateOptions);
    return result;
}
/**
 * Handle machine translation for fields marked as "translate"
 */ async function handleMachineTranslation(form, targetLanguage, rcbAuthenticationStore, metadata, translateOptions) {
    const translatablesMetadata = getTranslatableMetadata(metadata, translateOptions);
    console.log(`translatablesMetadata`, {
        translatablesMetadata
    });
    const objectValues = extractTranslatableValues(translatablesMetadata, form.getFieldsValue());
    const translationRequest = Object.values(objectValues);
    console.log(`translationRequest`, {
        translationRequest
    });
    if (translationRequest.length === 0) return "default";
    const templateType = form.getFieldValue(FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME);
    const templateIdentifier = form.getFieldValue(TemplateMetaDataField.Identifier);
    const { result } = await fetchTranslations(targetLanguage, translationRequest, rcbAuthenticationStore, {
        templateType,
        templateIdentifier
    });
    if (!result.isSupportedTargetLanguage) return "skip";
    applyTranslations(form, targetLanguage, objectValues, result, translatablesMetadata);
    return "success";
}
/**
 * Get metadata for fields that should be translated
 */ function getTranslatableMetadata(metadata, translateOptions) {
    return metadata.filter((option)=>{
        const propertyOption = translateOptions[option.propertyName];
        if (propertyOption === ETranslateOption.Translate) {
            return true; // If the option is defined to translate, return true
        }
        if ([
            ETranslateOption.Skip,
            ETranslateOption.Copy
        ].includes(propertyOption)) {
            return false; // If the option is defined to skip, return false
        }
        return true;
    });
}
/**
 * Apply translations to form fields
 */ function applyTranslations(form, targetLanguage, objectValues, result, translatablesMetadata) {
    Object.keys(objectValues).forEach((key, index)=>{
        const { targetNamePath, isObject, objectFlagNamePath } = buildTargetPath(key, targetLanguage, translatablesMetadata);
        const translatedValue = result.translations[index];
        updateFormField(form, targetNamePath, translatedValue, isObject, objectFlagNamePath);
    });
}
/**
 * Build the target path for a translation field
 */ function buildTargetPath(key, targetLanguage, translatablesMetadata) {
    let targetNamePath = [
        TRANSLATIONS_PROPERTY_NAME,
        targetLanguage,
        key
    ];
    let isObject = false;
    let objectFlagNamePath = [];
    if (key.includes(".")) {
        const paths = key.split(".");
        const mainPropertyKey = paths.shift();
        const keyMeta = translatablesMetadata.find((option)=>option.propertyName === mainPropertyKey);
        if (keyMeta?.isRelation && keyMeta.isRelationArray) {
            const arrayIndex = paths.shift();
            targetNamePath = [
                mainPropertyKey,
                arrayIndex,
                TRANSLATIONS_PROPERTY_NAME,
                targetLanguage,
                ...paths
            ];
        } else {
            isObject = true;
            objectFlagNamePath = [
                TRANSLATIONS_PROPERTY_NAME,
                targetLanguage,
                mainPropertyKey
            ];
            targetNamePath = [
                mainPropertyKey,
                TRANSLATIONS_PROPERTY_NAME,
                targetLanguage,
                ...paths
            ];
        }
    }
    return {
        targetNamePath,
        isObject,
        objectFlagNamePath
    };
}
/**
 * Update form field with new value and set translation flag
 */ function updateFormField(form, targetNamePath, translatedValue, isObject, objectFlagNamePath) {
    const currentValue = form.getFieldValue(targetNamePath);
    if (currentValue !== translatedValue) {
        form.setFieldValue(targetNamePath, translatedValue);
        const flagNamePath = isObject ? objectFlagNamePath : targetNamePath;
        form.setFieldValue(getTranslationFlagNamePath(flagNamePath), [
            ETranslationFlag.MachineTranslated
        ]);
    }
}
/**
 * Handle copying of fields marked as "copy"
 */ function handleFieldCopying(form, targetLanguage, translateOptions) {
    const copyFields = getCopyFieldsConfiguration(translateOptions, targetLanguage);
    copyFields.forEach(({ option, field, customTargetPath })=>{
        if (option === ETranslateOption.Copy) {
            const value = form.getFieldValue(field);
            const namePath = customTargetPath ?? defaultTranslationFlexNamePath(field)(targetLanguage);
            form.setFieldValue(namePath, value);
        }
    });
}
/**
 * Get configuration for fields that should be copied
 */ function getCopyFieldsConfiguration(translateOptions, targetLanguage) {
    return [
        {
            option: translateOptions?.name,
            field: TemplateMetaDataField.Name
        },
        {
            option: translateOptions?.headline,
            field: TemplateMetaDataField.Headline
        },
        {
            option: translateOptions?.subHeadline,
            field: TemplateMetaDataField.SubHeadline
        },
        {
            option: translateOptions?.provider,
            field: ServiceTemplateField.Provider,
            customTargetPath: [
                ServiceTemplateField.Provider,
                TRANSLATIONS_PROPERTY_NAME,
                targetLanguage
            ]
        },
        {
            option: translateOptions?.providerPrivacyPolicyUrl,
            field: ServiceTemplateField.ProviderPrivacyPolicyUrl
        },
        {
            option: translateOptions?.providerLegalNoticeUrl,
            field: ServiceTemplateField.ProviderLegalNoticeUrl
        }
    ];
}
export { FormFieldTemplateLanguages };
